<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { useFormValue } from "../../../../composables/namedValue";
import { randomID } from "../../../../utils/helpers";
import MLayoutStack from "../../MLayoutStack";

const props = defineProps<{
  modelValue?: any;
  name?: string;
  val?: any;

  label?: string;

  disabled?: boolean;
  disable?: boolean;

  tabindex?: string;

  ariaLabelledby?: string;
}>();

const instance = getCurrentInstance()!;
const templateValue = useFormValue(props);

const isRadioGroup = ref(false);
const tabindex = computed(() => {
  if (!isRadioGroup.value) return "0";
  // Selected radio is always reachable
  if (templateValue.value === props.val) return "0";
  if (templateValue.value != null) {
    // A value is set but doesn't match this radio. Keep the first radio (radio-index 0)
    // as a keyboard fallback in case no option matches the current value.
    const idx = (instance.proxy?.$el as HTMLElement)?.getAttribute("radio-index");
    return idx === "0" ? "0" : "-1";
  }
  return (instance.proxy?.$el as HTMLElement)?.getAttribute("radio-index") ?? "0";
});
onMounted(() => {
  isRadioGroup.value = instance.parent?.attrs.role === "radiogroup";
});

const forId = randomID("radio_group");
const inputRef = ref<HTMLInputElement | null>(null);

// Ensure el.checked stays in sync when the model changes externally
// (e.g. MRadioGroup deselect/reselect). v-model alone can lag a render cycle behind.
watch(
  () => templateValue.value !== props.val,
  (isUnselected) => {
    if (inputRef.value) {
      inputRef.value.checked = !isUnselected;
    }
  },
  { flush: "post" },
);

function onInputClick(ev: MouseEvent) {
  if (!ev.defaultPrevented) {
    templateValue.value = props.val;
  }
}

function onInputKeyup(ev: KeyboardEvent) {
  if ((ev.key === " " || ev.code === "Space") && !ev.defaultPrevented) {
    templateValue.value = props.val;
  }
}
</script>
<template>
  <m-layout-stack class="m-radio gap-1" no-gap tabindex="-1">
    <div class="m-radio__control">
      <div class="m-radio__inner">
        <input
          :id="forId"
          ref="inputRef"
          v-model="templateValue"
          class="m-radio__native"
          type="radio"
          :name="name"
          :value="val"
          :tabindex="tabindex"
          :disabled="disabled || disable"
          :aria-labelledby="ariaLabelledby || undefined"
          @click="onInputClick"
          @keyup="onInputKeyup"
        />
        <span class="m-radio__dot" />
      </div>
      <div class="m-radio__label">
        <label :for="forId" class="radio-label">{{ label }}</label>
      </div>
    </div>
    <div v-if="$slots.default && templateValue === val" class="input-value">
      <slot />
    </div>
  </m-layout-stack>
</template>

<style lang="scss">
.m-radio {
  .m-radio__control {
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    position: relative;
    overflow: visible;

    // ENG-21464: :focus-visible misses script-triggered auto-focus; data-auto-focus-active covers it.
    &:has(.m-radio__native:focus-visible)::before,
    &:has(.m-radio__native[data-auto-focus-active]:focus)::before {
      content: " ";
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      display: block;
      pointer-events: none;
      background: none;
      border-radius: var(--radius-default);
      box-shadow: 0 0 0 3px var(--focus-ring);
      z-index: 8000;
      overflow: visible;
    }
  }

  .m-radio__inner {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 19px;
    height: 19px;
    min-width: 19px;
    align-self: flex-start;
    margin-top: var(--space-1);
    pointer-events: initial;
  }

  .m-radio__native {
    appearance: none;
    -webkit-appearance: none;
    width: 19px;
    height: 19px;
    border-radius: var(--radius-pill);
    border: 2px solid var(--grey);
    background: var(--surface-default);
    cursor: pointer;
    position: absolute;
    inset: 0;
    margin: 0;

    &:focus {
      outline: none;
    }

    &:checked {
      background: var(--theme-blue);
      border-color: var(--theme-blue);

      & + .m-radio__dot {
        display: block;
      }
    }

    &:disabled {
      cursor: not-allowed;

      &:not(:checked) {
        border-color: var(--grey-light);
        background: var(--surface-default);
      }

      &:checked {
        opacity: 0.4;

        & + .m-radio__dot {
          opacity: 0.4;
        }
      }
    }
  }

  .m-radio__dot {
    display: none;
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    width: 7px;
    height: 7px;
    border-radius: var(--radius-circle);
    background: var(--surface-default);
    pointer-events: none;
  }

  .m-radio__label {
    margin-right: 1em;
    font-size: var(--font-size-md);
    line-height: 150%;
    font-weight: 400;
    color: var(--text-color);
    padding-left: var(--space-2);
  }

  .radio-label {
    display: block;
    padding: var(--space-1) 0;
    cursor: pointer;
  }

  .input-value {
    margin-left: var(--space-6);
    padding-bottom: var(--space-1);
  }

  &[disabled="disabled"],
  &.disabled {
    opacity: 1 !important;

    .radio-label {
      color: var(--grey) !important;
    }
  }
}
</style>
